import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { useIssueReportTopicPriorityField } from "../../../../components/Helpers"
import { UserContext } from "../../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../models/Authorization"
import {
  IssueReportTopicGet,
  IssueReportTopicPut,
} from "../../../../models/IssueReportTopic"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  context: z.string().min(1, "Context is required"),
  usefulLink: z
    .string()
    .optional()
    .refine(
      (val) =>
        !val ||
        /^https?:\/\/(?:[\w-]{1,63}\.)*abb\.com(?:\/[^\s?#]*)?(?:\?[^\s#]*)?$/.test(val),
      {
        message: "URL must be well-formed and end with 'abb.com'",
      }
    ),
  priority: z.number().min(0, "Priority is required"),
})

const returnURL = `/issue-reports#topics`

export function IssueReportTopicDetails(props: {
  readonly issueReportTopic: IssueReportTopicGet
  readonly setIssueReportTopic: (updatedTopic: IssueReportTopicGet) => void
}) {
  const { user, isLoadingUser } = useContext(UserContext)
  const { issueReportTopic, setIssueReportTopic } = props
  const applicationId = issueReportTopic.application.id

  const formMethods = useForm<IssueReportTopicPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      name: issueReportTopic.name,
      context: issueReportTopic.context,
      usefulLink: issueReportTopic.usefulLink,
      priority: issueReportTopic.priority,
    })
  }, [
    issueReportTopic.context,
    issueReportTopic.name,
    issueReportTopic.priority,
    issueReportTopic.usefulLink,
    reset,
  ])

  const onSubmit = useCallback(
    (values: IssueReportTopicPut) =>
      digitalManagerApi
        .put<IssueReportTopicGet>(`/issueReportTopics/${issueReportTopic.id}`, {
          name: values.name,
          context: values.context,
          usefulLink: values.usefulLink,
          priority: values.priority,
        })
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            toast.success("Issue report topic updated")
            setIssueReportTopic(res.data)
          }
        })
        .catch(() => {
          toast.error("Cannot update issue report topic")
          throw new Error("Cannot update issue report topic")
        }),
    [issueReportTopic.id, setIssueReportTopic]
  )

  const handleNameFieldChange = useCallback(
    (field: ControllerRenderProps<IssueReportTopicPut, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const handleContextFieldChange = useCallback(
    (field: ControllerRenderProps<IssueReportTopicPut, "context">) => (value: string) =>
      field.onChange(value),
    []
  )

  const handleUsefulLinkFieldChange = useCallback(
    (field: ControllerRenderProps<IssueReportTopicPut, "usefulLink">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderTextFieldName = useCallback(
    ({ field }: { field: ControllerRenderProps<IssueReportTopicPut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleNameFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleNameFieldChange]
  )

  const renderTextFieldContext = useCallback(
    ({ field }: { field: ControllerRenderProps<IssueReportTopicPut, "context"> }) => (
      <MUITextField
        id="context"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleContextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.context?.message === "string"
            ? { context: { message: errors.context.message } }
            : undefined
        }
      />
    ),
    [errors.context, handleContextFieldChange]
  )

  const renderTextFieldUsefulLink = useCallback(
    ({ field }: { field: ControllerRenderProps<IssueReportTopicPut, "usefulLink"> }) => (
      <MUITextField
        id="usefulLink"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleUsefulLinkFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.usefulLink?.message === "string"
            ? { usefulLink: { message: errors.usefulLink.message } }
            : undefined
        }
      />
    ),
    [errors.usefulLink, handleUsefulLinkFieldChange]
  )

  const renderPriorityTextField = useIssueReportTopicPriorityField<IssueReportTopicPut>({
    errors,
  })

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsContentProps: DiMaDetailsContentProps<IssueReportTopicPut> = {
    formMethods,
    inlineEdit: true,
    canWrite: hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      applicationId,
      DimaFeatureShortNameEnum.IR,
      AccessLevel.Write
    ),
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div className="title">{issueReportTopic.name}</div>,
        editItem: (
          <Controller name="name" control={control} render={renderTextFieldName} />
        ),
      },
      {
        id: 1,
        label: "Context",
        displayItem: <div className="title">{issueReportTopic.context}</div>,
        editItem: (
          <Controller name="context" control={control} render={renderTextFieldContext} />
        ),
      },
      {
        id: 2,
        label: "Useful Link",
        displayItem: <div className="title">{issueReportTopic.usefulLink || "-"}</div>,
        editItem: (
          <Controller
            name="usefulLink"
            control={control}
            render={renderTextFieldUsefulLink}
          />
        ),
      },
      {
        id: 3,
        label: "Priority",
        displayItem: <div className="title">{issueReportTopic.priority}</div>,
        editItem: (
          <Controller
            name="priority"
            control={formMethods.control}
            render={renderPriorityTextField}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
